import React from "react";
import { Link } from "react-router-dom";
import { isDesktopViewport } from "../utils/viewport";

/**
 * One weighing in the list. The parent already fetched it — daily gain included —
 * so there is nothing to load here.
 *
 * Tapping it opens the weighing's own page on mobile and the dialog on desktop.
 */
function WeightPreview(props) {
  const weight = props.weight || {};
  const GMQ = weight.gmq_interval || 0;

  const handleClick = (event) => {
    if (!isDesktopViewport()) return;
    event.preventDefault();
    props.setSelectedWeight(weight.pk_weights);
    props.setisUpdateModalOpen(true);
  };

  return (
    <Link
      to={"/poids/" + props.calfID + "/" + weight.pk_weights}
      onClick={handleClick}
      className="items-center p-1 flex cursor-pointer transition-all duration-200 hover:translate-x-2 border-b dark:border-slate-700"
    >
      <div className="my-3 mr-3 md:my-5 hidden sm:block">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="ml-3 h-4 w-4 dark:text-slate-400"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z"
          />
        </svg>
      </div>
      <div
        className={
          "flex w-full flex-col sm:flex-row sm:items-center "
        }
      >
        {/* First row: Weight, GMQ, and Date (on larger screens) */}
        <div className="flex w-full flex-row items-center justify-between">
          {/* Left section: Weight and GMQ grouped together */}
          <div className="flex flex-row items-center space-x-2 sm:space-x-4">
            {/* Weight section */}
            <div className="flex-shrink-0">
              <span className="font-medium dark:text-white tabular-nums text-sm md:text-base">
                {Number(weight.weight).toFixed(1)} kg
              </span>
            </div>

            {/* GMQ section */}
            <div className="flex-shrink-0">
              <div
                className={
                  "rounded-full w-fit px-2 " +
                  (GMQ < 0.75 && GMQ !== 0
                    ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300"
                    : GMQ === 0
                      ? "hidden"
                      : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300")
                }
              >
                <span className="text-sm font-medium tabular-nums">{GMQ} kg/jour</span>
              </div>
              <div
                className={
                  "rounded-full w-fit bg-blue-100 px-2 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 " +
                  (GMQ === 0 ? "" : "hidden")
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.38a48.474 48.474 0 00-6-.37c-2.032 0-4.034.125-6 .37m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.17c0 .62-.504 1.124-1.125 1.124H4.125A1.125 1.125 0 013 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 016 13.12M12.265 3.11a.375.375 0 11-.53 0L12 2.845l.265.265zm-3 0a.375.375 0 11-.53 0L9 2.845l.265.265zm6 0a.375.375 0 11-.53 0L15 2.845l.265.265z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Date section - aligned to the right on larger screens, hidden on small screens */}
          <div className="hidden sm:flex flex-shrink-0 items-center">
            <span className="text-sm text-stone-400 dark:text-slate-400">
              {new Date(weight.date).toLocaleString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="ml-1 h-4 w-4 text-stone-600 dark:text-slate-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </div>
        </div>

        {/* Second row: Date (on small screens only) */}
        <div className="flex sm:hidden w-full justify-end items-center mt-1">
          <span className="text-sm text-stone-400 dark:text-slate-400">
            {new Date(weight.date).toLocaleString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="ml-1 h-4 w-4 text-stone-600 dark:text-slate-400"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 4.5l7.5 7.5-7.5 7.5"
            />
          </svg>
        </div>
      </div>
    </Link>
  );
}

export default WeightPreview;
